
<template>
  <div
    :id="tender.tenderId"
    class="tender-card"
    :class="{'mb-4': !last, 'secondary__green_chosen': opened}"
    @click="openAll">
    <CreatedInOldAccountChip
      v-if="tender.hasRoute"
      :inner-style="'margin-top: -3px;'"
      :tender-id="tender.tenderId"/>
    <div class="card-text flex flex-wrap">
      <div class="flex width-100">
        <div
          ref="tender_name_col"
          class="pr-3 info-cols width-100">
          <div
            class="flex pb-1 h3-headline text-start">
            <router-link
              class="tender-name"
              data-qa="href-tenderitem"
              :target="target"
              :to="link">
              <div class="flex flex-wrap">
                <TooltipedText
                  :line-count="3"
                  :value="titleLabel"/>
                <sIcon
                  v-if="target"
                  class="ml-2"
                  line-height="20px"
                  size="14"
                  type="share"/>
              </div>
            </router-link>
          </div>
          <div class="pt-2 pb-1">
            <div class="h9-label pb-1">
              {{ $t('Tenders.TenderType') }}
            </div>
            <div
              class="text-left h7-text black--text"
              data-qa="biddingTypeTitle">
              {{ tender.biddingTypeTitle }}
            </div>
          </div>
          <div class="pt-2 pb-3">
            <div class="h9-label pb-1">
              {{ $t('Tenders.Category') }}
            </div>
            <div
              class="text-left h7-text black--text"
              data-qa="categoryTitle">
              {{ tender.categoryTitle }}
            </div>
          </div>
        </div>
        <div
          class="flex actions-container">
          <template
            v-if="loaded">
            <div
              class="status-block flex flex-wrap flex-direction-column"
              style="width: 216px">
              <div class="pr-3 flex flex-wrap flex-direction-column">
                <div class="h9-label mb-1">
                  {{ $t('Purchasing.Status') }}
                </div>
                <div class="flex flex-wrap align-center">
                  <StatusChip
                    data-qa="tender-card-status"
                    key-name="id"
                    :status="getStatus(tender)"
                    value-name="title"/>
                </div>
              </div>
              <div class="flex flex-wrap flex-direction-column">
                <div
                  v-if="stages && stages.length"
                  class="h9-label  mt-3 pb-1">
                  {{ $t('Purchasing.Approval') }}
                </div>
                <ListOfAcceptorsStages
                  class="mt-2"
                  data-qa="tender-card-list-of-acceptors"
                  :item="tender"
                  :loading="load_agreement"
                  :stages="stages"
                  @click-info="val => $emit('click-info', val)"/>
              </div>
            </div>
            <div class="align-start">
              <TenderActionsList
                class="align-start"
                data-qa="tender-card-actions-list"
                :element="tender"
                :not_editing="!can_edit"/>
            </div>
          </template>
        </div>
      </div>
      <div class="flex width-100 flex-between">
        <div
          class="pr-3 width-25">
          <div class="h9-label text-left pb-1">
            {{ $t('Tenders.ReceivingTo') }}
          </div>
          <TenderDateTo
            class="text-left flex flex-wrap"
            data-qa="tender-card-tender-date-to"
            :item="tender"/>
          <sTooltip
            v-if="tender.prolongationRuleTitle"
            :border="false"
            :open-delay="300"
            placement="bottom"
            popper-class="prolongationRuleTitle"
            type="white">
            <div
              class="h9-text-medium text-uppercase text-left">
              {{ $t('Tenders.WorksExtensionRule') }}
            </div>
            <span
              slot="content"
              data-qa="prolongationRuleTitle">
              {{ tender.prolongationRuleTitle }}
            </span>
          </sTooltip>
        </div>
        <div
          class="pr-3 width-25">
          <div class="h9-label mb-1">
            {{ show_scheduled ? $t('Tenders.ScheduledPublication') : $t('Tenders.PublicationDate') }}
          </div>
          <div
            v-if="publish_date"
            class="text-left h7-text black--text"
            data-qa="publishedDate">
            {{ publish_date }}
          </div>
          <div
            v-else
            class="text-left">
            –
          </div>
        </div>
        <div class="pr-3 width-25">
          <div class="h9-label mb-1">
            {{ $t('Catalog.DeliveryDate') }}
          </div>
          <div
            v-if="tender.deliveryDateStart"
            class="text-left h7-text black--text"
            data-qa="deliveryDateStart">
            {{ $t('Purchasing.From') }} {{ formatBy(tender.deliveryDateStart, 'DD.MM.YYYY' ) }}
          </div>
          <div
            v-if="tender.deliveryDateEnd"
            class="text-left h7-text black--text"
            data-qa="deliveryDateEnd">
            {{ $t('Common.till') }} {{ formatBy(tender.deliveryDateEnd, 'DD.MM.YYYY') }}
          </div>
          <div
            v-else
            class="text-left black--text"
            data-qa="tender-card-deliveryDateEnd">
            –
          </div>
        </div>
        <div class="width-25">
          <div class="h9-label mb-1">
            {{ $t('Tenders.Budget') }}
          </div>
          <div
            v-if="!biddingType.tenderingForPercentage && tender.tenderBudjet"
            class="text-left black--text h7-text"
            data-qa="tenderBudjet">
            {{ $n(tender.tenderBudjet) }} {{ tender.currencyTitle }}
          </div>
          <div
            v-else
            class="text-left black--text"
            data-qa="tender-card-tenderBudjet">
            –
          </div>
          <CurrencyConversionTooltip
            v-if="tender.tenderBudjet"
            :currency="tender.currencyTitle"
            :current-id="tender.nbuCurrency ? tender.nbuCurrency.currencyId : null"
            data-qa="currency"
            :nbu-currency="tender.nbuCurrency"
            :price="tender.tenderBudjet"/>
        </div>
        <div
          v-if="loaded"
          class="flex mt-auto comments align-bottom">
          <sTooltip
            v-if="$can('set', 'tenderViews')"
            :border="false"
            :content="$t('Tenders.Views1')"
            :open-delay="300"
            placement="bottom"
            popper-class="tender-viewsCount-tooltip info-tooltip"
            type="white">
            <component
              :is="tender.viewsCount ? 'router-link' : 'div'"
              v-bind="tender.viewsCount ? {to: {...link, name: 'tenderVisits'}, target: target} : {}"
              data-qa="href-viewsCount">
              <IconCount
                class="mr-2"
                :count="tender.viewsCount"
                icon="eye"
                :icon-color="!tender.viewsCount ? 'grey': ''"
                :text-class="{ 'grey--text': !tender.viewsCount }"/>
            </component>
          </sTooltip>
          <sTooltip
            :border="false"
            :content="$t('Tenders.Members')"
            :open-delay="300"
            placement="bottom"
            popper-class="tender-bidsCount-tooltip info-tooltip"
            type="white">
            <component
              :is="tender.bidsCount ? 'router-link' : 'div'"
              v-bind="tender.bidsCount ? {to: {...link, query: {tab: 'offers'}}, target: target} : {}"
              data-qa="href-tenderitem">
              <IconCount
                class="mr-2"
                :count="tender.bidsCount"
                icon="person-thin"
                :icon-color="!tender.bidsCount ? 'grey': ''"
                :text-class="{ 'grey--text': !tender.bidsCount }"/>
            </component>
          </sTooltip>
          <component
            :is="tender.questionsCount ? 'router-link' : 'div'"
            v-bind="tender.questionsCount ? {to: link, target: target} : {}"
            data-qa="href-tenderitem">
            <IconCount
              :count="tender.questionsCount"
              icon="comment"
              :icon-color="tender.questionWithoutResponse ? 'var(--color-error)' : !tender.questionsCount ? 'grey': 'var(--black-color)'"
              :text-class="{ 'grey--text': !tender.questionsCount, 'secondary__red--text': tender.questionWithoutResponse }"/>
          </component>
        </div>
      </div>
    </div>
    <v-expand-transition v-if="loaded">
      <div v-show="opened">
        <sDivider class="mt-1"/>
        <div
          v-if="tender.isUrgent"
          class="text-left">
          <sChip
            background="var(--color-disabled)"
            color="black"
            :text="$t('Tenders.UrgentPurchase')"/>
        </div>
        <div class="flex width-100">
          <div
            class="width-25 pr-3 pt-2">
            <div class="h9-label pb-1">
              {{ $t('Tenders.ContactPerson') }}
            </div>
            <div
              class="text-left h7-text"
              data-qa="contactPersonName">
              {{ tender.contactPersonName }}
            </div>
            <div class="h9-text grey--text py-1">
              <div
                class="text-left"
                data-qa="contactPersonPhone">
                {{ tender.contactPersonPhone }}
              </div>
              <div
                class="text-left"
                data-qa="contactPersonEmail">
                {{ tender.contactPersonEmail }}
              </div>
              <div
                class="text-left"
                data-qa="contactPersonDepartment">
                {{ tender.contactPersonDepartment || '-' }}
              </div>
            </div>
          </div>
          <div
            v-if="tender.organizer"
            class="width-25 pr-3 pt-2">
            <div class="h9-label pb-1">
              {{ $t('Tenders.Organizer') }}
            </div>
            <div
              class="text-left h7-text"
              data-qa="organizerName">
              {{ tender.organizer.title || '–' }}
            </div>
            <div
              class="text-left h8-text py-1 grey--text"
              data-qa="organizerUsreou">
              {{ tender.organizer.usreou || '–' }}
            </div>
          </div>
          <div
            class="width-25 pr-3 pt-2">
            <div class="h9-label pb-1">
              {{ $t('Tenders.DateOfCreation') }}
            </div>
            <div
              class="text-left h7-text"
              data-qa="tenderCreatedDate">
              {{ formatDateTime(tender.tenderCreatedDate) }}
            </div>
            <template v-if="tender.archiveDate">
              <div class="h9-label pb-1 mt-2">
                {{ $t('Tenders.TenderEndDate') }}
              </div>
              <div
                class="text-left h7-text"
                data-qa="ArchiveDate">
                {{ formatDateTime(tender.archiveDate) }}
              </div>
            </template>
          </div>
          <div
            class="width-25 pr-3 pt-2">
            <div class="h9-label pb-1">
              {{ $t('Tenders.Profit') }}
            </div>
            <div
              v-if="!biddingType.tenderingForPercentage && tender.economy"
              class="text-left h7-text"
              data-qa="economy">
              {{ $n(tender.economy) }} {{ tender.currencyTitle }}
            </div>
            <div
              v-else
              class="text-left">
              –
            </div>
          </div>
          <div
            class="width-25 pr-3 pt-2">
            <div class="h9-label pb-1">
              {{ $t('Tenders.purchaseAmount') }}
            </div>
            <div
              v-if="tender.purchaseAmount"
              class="text-left h7-text"
              data-qa="purchaseAmount">
              {{ $n(tender.purchaseAmount) }} {{ tender.currencyTitle }}
            </div>
            <div
              v-else
              class="text-left">
              –
            </div>
          </div>
        </div>
      </div>
    </v-expand-transition>
  </div>
</template>
<script>
import IconCount from 'components/common/elements/IconCount.vue'
import StatusChip from 'components/common/elements/StatusChip.vue'
import TenderDateTo from 'components/tenders/all tenders/TenderDateTo.vue'
import ListOfAcceptorsStages from 'components/agreement/ListOfAcceptorsStages.vue'
import CreatedInOldAccountChip from 'components/tenders/CreatedInOldAccountChip.vue'
import TenderActionsList from 'components/tenders/all tenders/TenderActionsList.vue'
import TooltipedText from 'components/common/blocks/tooltiped text/TooltipedText.vue'
import CurrencyConversionTooltip from 'components/common/elements/CurrencyConversionTooltip.vue'

import moment from 'moment'
import { modalMixin, tenderMixin, userMixin } from 'mixins'
import { mapGetters } from 'vuex'

export default {
  name: 'tenderCard',
  mixins: [modalMixin, tenderMixin, userMixin],
  data() {
    return {
      tenderSaleBiddingTypeIds: [3,47,91,37,92],
      opened: false,
      can_edit: false,
      observer: null,
      loaded: false,
    }
  },
  props: {
    tender: Object,
    last: Boolean,
    load_agreement: Boolean,
    wait_load: Boolean,
    stages: Array
  },
  beforeDestroy() {
    if ( this.observer && !this.loaded ) {
      this.observer.unobserve( this.$el )
    }
  },
  async mounted() {

    if ( this.wait_load ) {
      let options = {
        root: document.querySelector('.main_container'),
        rootMargin: '400px',
        threshold: 0.2
      }

      this.observer = new IntersectionObserver(elements => { //https://developer.mozilla.org/ru/docs/Web/API/Intersection_Observer_API
        elements.forEach(element => {
          if ( element.isIntersecting && !this.loaded ) {
            this.observer.unobserve(element.target)
            this.observer = null
          }
          this.loaded ||= element.isIntersecting
        })
      }, options)
      this.observer.observe(this.$el)
    } else {
      this.loaded = true
    }

  },
  computed: {
    ...mapGetters({
      biddingTypes: 'getBiddingTypes'
    }),
    tendersForSale() {
      return this.tenderSaleBiddingTypeIds.includes(this.tender.biddingTypeId)

    },
    target() {
      return this.org_id === this.storedCurrentOrganization.organizationItId ? '' : '_blank'
    },
    tender_id() {
      return this.tender.tenderId
    },
    org_id() {
      return  this.tender?.organizer?.organizationItId || this.$route.params.org
    },
    link() {
      return {name: 'tenderitem', params: { id: this.tender_id, org: this.org_id}}
    },
    biddingType() {
      let biddingType
      if (this.tender?.biddingTypeId) {
        biddingType = this.biddingTypes?.find(i => i.biddingTypeId === this.tender.biddingTypeId) || {}
        biddingType.biddingTypeId = this.tender.biddingTypeId
      }
      return biddingType || {}
    },
    titleLabel() {
      return `#${this.tender.tenderNumber}.  ${this.tender.tenderSubject}`
    },
    is_on_registration() {
      return this.tender?.tenderStatusId == 1
    },
    scheduled_date() {
      let scheduled = this.tender?.tenderPublishedSchedule
      return this.is_on_registration && scheduled?.isScheduled && scheduled?.scheduledDate
    },
    show_scheduled() {
      return this.scheduled_date  && ( !this.tender.publishedDate || moment( this.tender.publishedDate ).format('x') < moment( this.scheduled_date ).format('x'))
    },
    publish_date() {
      let result = this.tender.publishedDate
      if ( this.scheduled_date ) {
        if ( this.show_scheduled ) {
          result = this.scheduled_date
        }
      }
      return this.formatDateTime( result )
    }
  },
  methods: {
    openAll() {
      this.opened = !this.opened
    },
    formatDateTime(value) {
      if (value) {
        return moment(value).format('DD.MM.YYYY, HH:mm ')
      } else {
        return '–'
      }
    }
  },
  watch: {
    stages: {
      immediate: true,
      deep: true,
      handler(newValue, oldValue) {
        if (!oldValue || JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
          const agreements = newValue?.map(a => a.agreements).flat(1)
          if (this.tender.organizer.organizationItId !== +this.storedCurrentOrganization.organizationItId) {
            this.can_edit = false
          } else {
            this.can_edit = this.$_checkCanEdit(this.tender, agreements, newValue)
          }
        }
      }
    }
  },
  components: {
    IconCount,
    StatusChip,
    TenderDateTo,
    TooltipedText,
    TenderActionsList,
    ListOfAcceptorsStages,
    CreatedInOldAccountChip,
    CurrencyConversionTooltip
  }
}
</script>
<style lang="scss">
.tender-card {
  position: relative;
  background-color: var(--v-main__light_green-base);
  border-color: var(--v-main__light_green-base);
  padding-right: 32px !important;
  padding-left: 32px !important;
  padding-bottom: 24px;
  padding-top: 24px;
  cursor: pointer;
  .created-in-old-account-chip {
    position: absolute;
    top: 6px;
    left: 32px;
  }
}
</style>
